import { HitModel, HitterSide, OutcomeCode } from '../../enums/hitters.enums';
import { BallType } from '../../enums/machine.enums';
import { DictOf } from '../../interfaces/common/i-dictionary';
import { Nullable } from '../common/i-generics';
import { ITrajectory } from '../pitches';

// comes from Vision via MSBS or filled by call to TDS endpoint if missing
interface ComputedFields {
  outcome: Nullable<OutcomeCode>;
  outcomeProbs: DictOf<number>; // key is OutcomeCode, value is probabilities
  distanceFT: Nullable<number>;
  hLandingDEG: Nullable<number>;
  usedExitSpeedMPH: number;
  exitOmegaRPM: Nullable<number[]>;
  pathPoints: Nullable<number[][]>;
  timeStep: Nullable<number>;
  landingTime: Nullable<number>;

  // contact fields
  xbacon: Nullable<number>; // Expected Batting Average on contact
  xwobacon: Nullable<number>; // Expected Weighted On-Base Average on contact

  model: Nullable<HitModel>;
}

export const COMPUTED_FIELD_KEYS = Object.keys({
  outcome: 1,
  outcomeProbs: 1,
  distanceFT: 1,
  hLandingDEG: 1,
  usedExitSpeedMPH: 1,
  exitOmegaRPM: 1,
  pathPoints: 1,
  timeStep: 1,
  landingTime: 1,
  xbacon: 1,
  xwobacon: 1,
  model: 1,
} as const satisfies Record<
  keyof ComputedFields,
  1
>) as (keyof ComputedFields)[];

interface IBuildHitOptions {
  // aka: MlbVenue > id, the location that the hitter was training for
  venueID: number;
  ballType: BallType;
  hitterSide: HitterSide;
}

export interface IBuildHitRequest {
  // ! deprecate legacy hit results
  compareLegacy?: boolean;
  payloads: IBuildHitPayload[];
}

export interface IBuildHitPayload {
  // corresponds to index in result
  index: number;
  hit: Partial<ISHHitDetails>;
  pitchTrajectory: Nullable<ITrajectory>;
  options?: Partial<IBuildHitOptions>;
}

// take this from python => update hit details accordingly
export interface IBuildHitResult extends ComputedFields {
  index: number; // corresponds to index in payload
}

export interface IBuildHitResults {
  results: IBuildHitResult[];
  legacyResults?: IBuildHitResult[];
}

// UI will use these (e.g. instead of having to coalesce outcome override > outcome everywhere)
interface MixinFields {
  _mx_outcome: Nullable<OutcomeCode>; // override if any, otherwise hit.outcome
  _mx_swing: Nullable<boolean>; // override if any, otherwise hit.swing
}

interface InputFields {
  outcome_override: Nullable<OutcomeCode>;
  swing_override: Nullable<boolean>;
}

export interface ISHHitDetails
  extends ComputedFields, InputFields, MixinFields {
  // system fields
  invalid: boolean; // e.g. if distanceFT exceeds MAX_HIT_DISTANCE_FT
  processed?: Nullable<boolean>; // if we get hit_and_swing_time_from_fire_ts, FW is done processing the hit

  // detection fields
  swing: Nullable<boolean>; // whether a swing was detected or not, null if swing detection is disabled/not available
  exitMPH: Nullable<number>;
  vLaunchDEG: Nullable<number>;
  hLaunchDEG: Nullable<number>;
}

export const EMPTY_SESSION_HIT_DETAILS: ISHHitDetails = {
  invalid: false,
  swing: null,
  exitMPH: null,
  vLaunchDEG: null,
  hLaunchDEG: null,
  distanceFT: null,
  outcome: null,
  hLandingDEG: null,
  model: null,
  exitOmegaRPM: null,
  landingTime: null,
  pathPoints: null,
  timeStep: null,
  // inputs
  swing_override: null,
  outcome_override: null,
  // mixins
  _mx_outcome: null,
  _mx_swing: null,
  xbacon: null,
  xwobacon: null,
  outcomeProbs: {},
  usedExitSpeedMPH: 0,
};
